import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Settings2, 
  ArrowUpCircle, 
  FileText, 
  BarChart3,
  Bell,
  Activity,
  Monitor,
  Server,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const Settings = () => {
  const { hasPermission } = useAuth();
  const [systemHealth, setSystemHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch system health on component mount
  useEffect(() => {
    fetchSystemHealth();
  }, []);

  const fetchSystemHealth = async () => {
    try {
      const response = await api.get('/health');
      setSystemHealth(response.data);
    } catch (error) {
      console.error('Failed to fetch system health:', error);
      setSystemHealth({
        status: 'error',
        message: 'Failed to connect to system',
        timestamp: new Date().toISOString(),
        version: 'Unknown',
        developer: 'System Unavailable',
        environment: 'Unknown'
      });
    } finally {
      setLoading(false);
    }
  };

  const menuCategories = {
    system: {
      title: 'System Configuration',
      description: 'Manage core system settings and configurations',
      items: [
        {
          id: 'system-config',
          label: 'System Configuration',
          icon: Server,
          path: '/settings/system',
          description: 'Configure system-wide settings and parameters',
          permissions: ['config.view', 'manage-system']
        },
        {
          id: 'escalation',
          label: 'Escalation Settings',
          icon: ArrowUpCircle,
          path: '/settings/escalation',
          description: 'Configure ticket escalation rules and hierarchy',
          permissions: ['escalation.view', 'escalation.update']
        }
      ]
    },
    analytics: {
      title: 'Reports & Analytics',
      description: 'Access performance reports and system analytics',
      items: [
        {
          id: 'staff-reports',
          label: 'Staff Reports',
          icon: BarChart3,
          path: '/settings/staff-reports',
          description: 'View staff performance reports and analytics',
          permissions: ['reports.view']
        },
        {
          id: 'ticket-logs',
          label: 'Ticket Logs',
          icon: FileText,
          path: '/settings/logs',
          description: 'View system and ticket activity logs',
          permissions: ['logs.view', 'manage-system']
        }
      ]
    },
    monitoring: {
      title: 'System Monitoring',
      description: 'Monitor system health and performance',
      items: [
        {
          id: 'notifications',
          label: 'Notification Settings',
          icon: Bell,
          path: '/notifications',
          description: 'Configure system notifications and alerts',
          permissions: ['notifications.view']
        }
      ]
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <Settings2 className="w-6 h-6 mr-2" />
          Settings
        </h1>
        <p className="text-gray-600 mt-1">Manage system configuration and settings</p>
      </div>

      {/* Settings Categories */}
      {Object.entries(menuCategories).map(([categoryKey, category]) => {
        // Filter items based on permissions
        const availableItems = category.items.filter(item => {
          return item.permissions.some(permission => hasPermission(permission));
        });

        // Don't show category if no items are available
        if (availableItems.length === 0) {
          return null;
        }

        return (
          <div key={categoryKey} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-6">
              <h2 className="text-lg font-medium text-gray-900">{category.title}</h2>
              <p className="text-sm text-gray-600 mt-1">{category.description}</p>
            </div>
            
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {availableItems.map((item) => {
                const IconComponent = item.icon;

                return (
                  <Link
                    key={item.id}
                    to={item.path}
                    className="block p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all duration-200 group"
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-1">
                        <IconComponent className="w-6 h-6 text-blue-600 group-hover:text-blue-700" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-gray-900 group-hover:text-blue-700">
                          {item.label}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                          {item.description}
                        </p>
                        {/* Permission indicator */}
                        <div className="flex flex-wrap gap-1 mt-2">
                          {item.permissions.slice(0, 2).map((permission, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                            >
                              {permission.split('.')[0]}
                            </span>
                          ))}
                          {item.permissions.length > 2 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                              +{item.permissions.length - 2}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* System Information */}
      {hasPermission('manage-system') && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Monitor className="w-5 h-5 mr-2" />
            System Information
          </h2>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading system information...</span>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-blue-900 mb-2">Application Version</h3>
                <p className="text-lg font-bold text-blue-700">
                  {systemHealth?.version ? `v${systemHealth.version}` : 'Unknown'}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  {systemHealth?.environment || 'Production'} environment
                </p>
              </div>
              
              <div className={`rounded-lg p-4 ${
                systemHealth?.status === 'success' ? 'bg-green-50' : 'bg-red-50'
              }`}>
                <h3 className={`text-sm font-medium mb-2 ${
                  systemHealth?.status === 'success' ? 'text-green-900' : 'text-red-900'
                }`}>
                  System Status
                </h3>
                <div className="flex items-center">
                  {systemHealth?.status === 'success' ? (
                    <Activity className="w-4 h-4 text-green-600 mr-1" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-red-600 mr-1" />
                  )}
                  <p className={`text-lg font-bold ${
                    systemHealth?.status === 'success' ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {systemHealth?.status === 'success' ? 'Operational' : 
                     systemHealth?.status ? systemHealth.status.charAt(0).toUpperCase() + systemHealth.status.slice(1) : 'Unknown'}
                  </p>
                </div>
                <p className={`text-xs mt-1 ${
                  systemHealth?.status === 'success' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {systemHealth?.message || 'No status message available'}
                </p>
              </div>
              
              <div className="bg-purple-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-purple-900 mb-2">Developer</h3>
                <p className="text-lg font-bold text-purple-700">
                  {systemHealth?.developer || 'Unknown'}
                </p>
                <p className="text-xs text-purple-600 mt-1">
                  {systemHealth?.timestamp ? 
                    `Last updated: ${new Date(systemHealth.timestamp).toLocaleTimeString()}` : 
                    'No timestamp available'
                  }
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Settings;